import classNames from 'classnames';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import ReactSelect from 'react-select';

import {
	Control,
	CustomValueContainer,
	IndicatorsContainer,
	IndicatorSeparator,
	LoadingIndicator,
	Menu,
	MenuListWithBulkSelect,
	MultiValue as MultiValueComp,
	Option,
	SingleValue,
	ValueContainer,
} from 'components/Select/subcomponents';
import { Tooltip } from 'components';

import type { MultiSelectProps } from 'components/Select/types';
import type { Ref } from 'react';
import type { ActionMeta, MultiValue, SelectInstance } from 'react-select';

function MultiSelectWithRef<Option = unknown>(
	{
		label,
		hideLabel = false,
		options,
		getOptionLabel,
		getOptionValue,
		onChange,
		sizeX = 'full',
		sizeY = 'sm',
		errorMessage,
		errorMessageArea = 'none',
		isWorking = false,
		isKnownValid = false,
		instructions,
		value,
		defaultValue,
		disabled,
		reactSelectProps,
	}: MultiSelectProps<Option>,
	forwardedRef: Ref<SelectInstance<Option, true>>
) {
	// --- 1) De-dupe options by label (your original logic) ---
	const originalOptions = options;
	const seenLabels = new Set<string>();
	const virtualOptions: Option[] = originalOptions.reduce((acc: any, option: any) => {
		const currentLabel = option.label;
		if (!seenLabels.has(currentLabel)) {
			seenLabels.add(currentLabel);
			acc.push(option);
		}
		return acc;
	}, []);

	// --- 2) De-dupe incoming values by label (your original logic) ---
	const originalValues = Array.isArray(value) ? value : [];
	const seenValues = new Set<string>();
	const virtualValuesRaw: Option[] = originalValues.reduce((acc: any, option: any) => {
		const currentLabel = (option as any).label;
		if (!seenValues.has(currentLabel)) {
			seenValues.add(currentLabel);
			acc.push(option);
		}
		return acc;
	}, []);

	// --- 3) Track and enforce selection order by label ---
	// We key off .label to match your de-dupe behavior.
	const selectionOrderRef = useRef<string[]>([]);

	// Initialize order from defaultValue / current controlled value (first render + whenever value shape changes)
	useEffect(() => {
		const labels = (virtualValuesRaw as any[]).map((v) => v.label);
		// Only set if our current order is missing some or the set changed meaningfully
		// Keep existing relative order; append any new labels at the end.
		const current = selectionOrderRef.current;
		const next = current.filter((l) => labels.includes(l)).concat(labels.filter((l) => !current.includes(l)));
		selectionOrderRef.current = next;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [/* ensure re-run when external value changes */ originalValues.length]);

	// Order helper
	const sortBySelectionOrder = (arr: Option[]) => {
		const order = selectionOrderRef.current;
		// Unknown labels go to the end, preserving their relative order.
		return [...arr].sort((a: any, b: any) => {
			const ai = order.indexOf(a.label);
			const bi = order.indexOf(b.label);
			const aRank = ai === -1 ? Number.POSITIVE_INFINITY : ai;
			const bRank = bi === -1 ? Number.POSITIVE_INFINITY : bi;
			return aRank - bRank;
		});
	};

	// The final, ordered values we pass into ReactSelect
	const virtualValuesOrdered: Option[] = useMemo(
		() => sortBySelectionOrder(virtualValuesRaw),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[virtualValuesRaw.length]
	);

	// --- 4) onChange wrapper: maintain order + map back to originalOptions labels ---
	const onChangeWrapper = (props: MultiValue<Option>, actionMeta: ActionMeta<any>) => {
		// Update order based on user action
		const order = selectionOrderRef.current.slice();

		switch (actionMeta.action) {
			case 'select-option': {
				const selectedLabel = (actionMeta.option as any)?.label;
				if (selectedLabel && !order.includes(selectedLabel)) {
					order.push(selectedLabel);
				}
				break;
			}
			case 'remove-value': {
				const removedLabel = (actionMeta.removedValue as any)?.label;
				if (removedLabel) {
					const idx = order.indexOf(removedLabel);
					if (idx > -1) order.splice(idx, 1);
				}
				break;
			}
			case 'pop-value': {
				const removedLabel = (actionMeta.removedValue as any)?.label;
				if (removedLabel) {
					const idx = order.indexOf(removedLabel);
					if (idx > -1) order.splice(idx, 1);
				}
				break;
			}
			case 'clear': {
				order.length = 0;
				break;
			}
			default:
				break;
		}

		selectionOrderRef.current = order;

		if (!onChange) return;

		// Sort the selected "virtual" options by our maintained order before mapping
		const orderedVirtual = sortBySelectionOrder(props as Option[]);

		// Map selected virtual options back to ALL original options with matching labels
		const mappedValues = orderedVirtual.flatMap((selectedOption: any) =>
			originalOptions.filter((o: any) => o.label === selectedOption.label)
		);

		onChange(mappedValues as any, actionMeta);
	};

	return (
		<label className={classNames('relative', sizeX === 'full' ? 'block' : 'inline-block')}>
			<div
				className={classNames('mb-2', {
					hidden: hideLabel,
				})}
			>
				<span className='font-preset-label'>{label}</span>
				{instructions && (
					<Tooltip content={instructions}>
						<span className='material-symbol-[1rem] ml-2 align-bottom cursor-help'>info</span>
					</Tooltip>
				)}
			</div>

			<ReactSelect
				className={classNames('font-primary', reactSelectProps?.className, {
					'text-p3': sizeY === 'sm',
					'text-p2': sizeY === 'md',
					'text-p1': sizeY === 'lg',
					'opacity-50 !pointer-events-auto cursor-not-allowed': disabled,
				})}
				options={virtualOptions}
				getOptionLabel={getOptionLabel}
				getOptionValue={getOptionValue}
				onChange={onChangeWrapper}
				value={virtualValuesOrdered}
				defaultValue={defaultValue}
				isLoading={isWorking}
				isDisabled={disabled}
				isMulti
				// closeMenuOnSelect={false}
				hideSelectedOptions={false}
				backspaceRemovesValue={false}
				// openMenuOnClick
				// openMenuOnFocus
				// blurInputOnSelect={false}
				isClearable={false}
				menuShouldScrollIntoView={false}
				// Completely remove the styles for each component we want to use Tailwind classes on.
				styles={{
					control: () => ({}),
					valueContainer: () => ({}),
					multiValue: () => ({}),
					indicatorsContainer: () => ({}),
					dropdownIndicator: () => ({}),
					menu: () => ({}),
					menuList: () => ({}),
					option: () => ({}),
					singleValue: () => ({}),
				}}
				// Reimplement specific subcomponents to control their styles and functionality.
				components={{
					Control,
					ValueContainer: CustomValueContainer,
					MultiValue: MultiValueComp,
					IndicatorsContainer,
					IndicatorSeparator,
					LoadingIndicator,
					Menu,
					MenuList: MenuListWithBulkSelect,
					Option,
					SingleValue,
				}}
				{...reactSelectProps}
				wrapperProps={{
					label,
					hideLabel,
					options,
					getOptionLabel,
					getOptionValue,
					sizeX,
					sizeY,
					errorMessage,
					errorMessageArea,
					isWorking,
					isKnownValid,
					instructions,
					disabled,
				}}
				ref={forwardedRef}
			/>

			<p
				className={classNames('mt-2 font-primary text-p2 text-red-400', {
					'absolute top-full': errorMessageArea === 'floating',
					hidden: !errorMessage && errorMessageArea !== 'reserved',
				})}
			>
				{errorMessage}
				{!errorMessage && errorMessageArea === 'reserved' && <>&nbsp;</>}
			</p>
		</label>
	);
}

// The `forwardRef` function doesn't play nicely with generic types, which we
// need in order to benefit from type safety when using React Select. Cast the
// ref-forwarded component back to its original type to prevent unexpected
// issues with the props provided by React Select.
const OrderedMultiSelect = forwardRef(MultiSelectWithRef) as typeof MultiSelectWithRef;

export { OrderedMultiSelect, OrderedMultiSelect as default };
